import { Colors, Label, SelectFilled, Text, TextInput } from '@amelio/ui-library';
import { useId } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import {
    AspirationFollowUp,
    AspirationQuestionBlock,
    FollowUpLabel,
} from './AspirationQuestionBlock';
import {
    GROWTH_FIELD_LABEL,
    GROWTH_FOLLOW_UPS_OFFSET,
    GROWTH_SELECT_BORDER,
} from './careerAspirationStyles';
import {
    DESIRED_POSITION_MAX_LENGTH,
    isNewRolePath,
    NO_PROFILE_VALUE,
} from './careerAspirationValidation';
import { importanceLevelKey } from './importanceLevelLabel';
import { useCareerAspirationFormText } from './useCareerAspirationFormText';

import type { CareerAspirationFormValues } from './careerAspirationValidation';
import type { PathAwareQuestionProps } from './types';
import type { OptionProps } from '@amelio/ui-library';

import { useGetCompetencyProfiles } from '@/api/competency-services/competency-profile';
import { ChoiceChips, toggleSingle } from '@/components/ChoiceChips/ChoiceChips';
import {
    ACCEPTS_NEW_RESPONSIBILITIES_VALUES,
    DEVELOPMENT_PATHS,
    IMPORTANCE_LEVELS,
} from '@/types/career-growth';
import { getLocalizedText } from '@/utils/localization';

/** The path the employee sees ahead, then what that path asks about the role it aims at. */
export const DevelopmentPathQuestion = ({
    employeeFirstName,
    developmentPath,
    dataTestId,
}: PathAwareQuestionProps) => {
    const { t, optionsOf } = useCareerAspirationFormText();
    const { control } = useFormContext<CareerAspirationFormValues>();

    const question = t('performanceProfile.growth.aspiration.developmentPath', {
        name: employeeFirstName,
    });

    return (
        <AspirationQuestionBlock
            title={question}
            dataTestId={`${dataTestId}-development-path`}
        >
            <Controller
                name={'developmentPath'}
                control={control}
                render={({ field }) => (
                    <ChoiceChips
                        options={optionsOf(
                            DEVELOPMENT_PATHS,
                            (path) => `performanceProfile.growth.aspiration.paths.${path}`,
                        )}
                        selected={field.value ? [field.value] : []}
                        onToggle={(value) => field.onChange(toggleSingle(field.value, value))}
                        ariaLabel={question}
                        dataTestId={`${dataTestId}-path`}
                    />
                )}
            />

            {developmentPath && (
                <PathFollowUps
                    employeeFirstName={employeeFirstName}
                    developmentPath={developmentPath}
                    dataTestId={dataTestId}
                />
            )}
        </AspirationQuestionBlock>
    );
};

/**
 * What the picked path asks for. The competency profile it aims at is asked on every path -- a
 * manager who deepens an expertise or is still exploring may already have one in mind, and naming
 * it is never required. The position, how much it matters and the responsibilities it would bring
 * belong to the paths that aim at a new role, the way the meeting only asks them then.
 *
 * Every answer of this form stays behind the manager scope, the profile included: the employee's own
 * portal reads the target career step the manager planned, never this one
 * (`EmployeeEvolutionService.GetForCurrentEmployeeAsync`). Nothing here is said to promise otherwise.
 */
const PathFollowUps = ({
    employeeFirstName,
    developmentPath,
    dataTestId,
}: PathAwareQuestionProps) => {
    const { t, optionsOf, errorMessage } = useCareerAspirationFormText();
    const fieldId = useId();
    const {
        control,
        formState: { errors },
    } = useFormContext<CareerAspirationFormValues>();

    // Mounted only once a path is picked, so the profiles are only read then.
    const { data: profiles } = useGetCompetencyProfiles();
    const profileOptions: OptionProps[] = [
        {
            value: NO_PROFILE_VALUE,
            label: t('performanceProfile.growth.aspiration.form.noProfile'),
        },
        ...(profiles ?? []).map((profile) => ({
            value: profile.id,
            label: getLocalizedText(profile.name),
        })),
    ];

    const showNewRoleFields = isNewRolePath(developmentPath);
    const importanceQuestion = t('performanceProfile.growth.aspiration.importance', {
        name: employeeFirstName,
    });
    const responsibilitiesQuestion = t('performanceProfile.growth.aspiration.responsibilities');

    return (
        <>
            {/* The second column only exists when the position shares the row: alone, the profile
                keeps the whole width rather than leaving half a row empty beside it. */}
            <div
                className={
                    showNewRoleFields
                        ? `${GROWTH_FOLLOW_UPS_OFFSET} grid gap-4 md:grid-cols-2`
                        : `${GROWTH_FOLLOW_UPS_OFFSET} grid gap-4`
                }
            >
                {showNewRoleFields && (
                    <AspirationFollowUp
                        label={
                            <Label
                                htmlFor={`${fieldId}-position`}
                                color={Colors.NAVY}
                                className={GROWTH_FIELD_LABEL}
                            >
                                {t('performanceProfile.growth.aspiration.positionTitle')}
                            </Label>
                        }
                    >
                        <Controller
                            name={'desiredPositionTitle'}
                            control={control}
                            render={({ field }) => (
                                <TextInput
                                    id={`${fieldId}-position`}
                                    value={field.value}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    placeholder={t(
                                        'performanceProfile.growth.aspiration.form.positionPlaceholder',
                                    )}
                                    maxLength={DESIRED_POSITION_MAX_LENGTH}
                                    variant={'white'}
                                    bordered
                                    error={!!errors.desiredPositionTitle}
                                    errorMessage={errorMessage(
                                        errors.desiredPositionTitle?.message,
                                    )}
                                    dataTestId={`${dataTestId}-position`}
                                />
                            )}
                        />
                    </AspirationFollowUp>
                )}

                <AspirationFollowUp
                    label={
                        <FollowUpLabel>
                            {t('performanceProfile.growth.aspiration.competencyProfile')}
                        </FollowUpLabel>
                    }
                    hint={t('performanceProfile.growth.aspiration.form.profileHint')}
                >
                    <Controller
                        name={'desiredCompetencyProfileId'}
                        control={control}
                        render={({ field }) => (
                            <SelectFilled
                                options={profileOptions}
                                value={field.value}
                                onChange={field.onChange}
                                sort={'none'}
                                searchable
                                searchPlaceholder={t(
                                    'performanceProfile.growth.aspiration.form.searchProfile',
                                )}
                                placeholder={t(
                                    'performanceProfile.growth.aspiration.form.profilePlaceholder',
                                )}
                                variant={'white'}
                                triggerBorderClassName={GROWTH_SELECT_BORDER}
                                ariaLabel={t(
                                    'performanceProfile.growth.aspiration.competencyProfile',
                                )}
                                dataTestId={`${dataTestId}-profile`}
                                emptyText={t('common.noResults')}
                            />
                        )}
                    />
                </AspirationFollowUp>
            </div>

            {showNewRoleFields && (
                <>
                    <AspirationFollowUp
                        label={<FollowUpLabel>{importanceQuestion}</FollowUpLabel>}
                        hint={t('performanceProfile.growth.aspiration.form.importanceScale')}
                    >
                        <Controller
                            name={'importanceLevel'}
                            control={control}
                            render={({ field }) => (
                                <div className={'flex flex-wrap items-center gap-4'}>
                                    <ChoiceChips
                                        options={IMPORTANCE_LEVELS.map((level) => ({
                                            value: String(level),
                                            label: String(level),
                                        }))}
                                        selected={field.value === null ? [] : [String(field.value)]}
                                        onToggle={(value) =>
                                            field.onChange(
                                                field.value === Number(value)
                                                    ? null
                                                    : Number(value),
                                            )
                                        }
                                        ariaLabel={importanceQuestion}
                                        shape={'round'}
                                        dataTestId={`${dataTestId}-importance`}
                                    />
                                    {field.value !== null && (
                                        <Text
                                            color={Colors.NAVY}
                                            weight={'medium'}
                                        >
                                            {t(importanceLevelKey(field.value))}
                                        </Text>
                                    )}
                                </div>
                            )}
                        />
                    </AspirationFollowUp>

                    <AspirationFollowUp
                        label={<FollowUpLabel>{responsibilitiesQuestion}</FollowUpLabel>}
                    >
                        <Controller
                            name={'acceptsNewResponsibilities'}
                            control={control}
                            render={({ field }) => (
                                <ChoiceChips
                                    options={optionsOf(
                                        ACCEPTS_NEW_RESPONSIBILITIES_VALUES,
                                        (answer) =>
                                            `performanceProfile.growth.aspiration.responsibilityOptions.${answer}`,
                                    )}
                                    selected={field.value ? [field.value] : []}
                                    onToggle={(value) =>
                                        field.onChange(toggleSingle(field.value, value))
                                    }
                                    ariaLabel={responsibilitiesQuestion}
                                    dataTestId={`${dataTestId}-responsibilities`}
                                />
                            )}
                        />
                    </AspirationFollowUp>
                </>
            )}
        </>
    );
};
